/**
 * OAuth connect button component.
 * Handles OAuth flow initiation and connection status.
 */

// React Imports
import { useState } from 'react';
import { Button, CircularProgress, Alert, Box } from '@mui/material';
import {
  Link as LinkIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { connectorService } from '../../services/connectorService';
import { useNotificationStore } from '../../store/notificationStore';
import { ConnectionStatusIndicator, type ConnectionStatus } from './ConnectionStatusIndicator';

export interface OAuthConnectButtonProps {
  instanceId: string;
  connectorName: string;
  isConnected?: boolean;
  onConnected?: () => void;
  onError?: (error: string) => void;
}

/**
 * OAuth connect button component.
 */
export const OAuthConnectButton = ({
  instanceId,
  connectorName,
  isConnected = false,
  onConnected,
  onError,
}: OAuthConnectButtonProps) => {
  const { addNotification } = useNotificationStore();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<ConnectionStatus>(isConnected ? 'Connected' : 'Disconnected');

  const handleConnect = async () => {
    setLoading(true);
    try {
      const response = await connectorService.initiateOAuth(instanceId);
      
      // Open OAuth authorization URL in popup or redirect
      if (response.authorizationUrl) {
        // Store state for callback verification
        sessionStorage.setItem(`oauth_state_${instanceId}`, response.state);
        
        // Open popup window for OAuth
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const popup = window.open(
          response.authorizationUrl,
          'OAuth Authorization',
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );

        // Listen for OAuth callback (this would typically be handled via a callback route)
        // For now, we'll use a polling mechanism or postMessage
        if (popup) {
          const checkClosed = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkClosed);
              setLoading(false);
              // Check if connection was successful by testing the instance
              checkConnectionStatus();
            }
          }, 1000);
        }
      } else {
        throw new Error('No authorization URL returned');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Failed to initiate OAuth flow';
      setStatus('Error');
      addNotification(message, 'error');
      onError?.(message);
      setLoading(false);
    }
  };

  const handleReconnect = async () => {
    setStatus('Disconnected');
    await handleConnect();
  };

  const checkConnectionStatus = async () => {
    try {
      const instance = await connectorService.getConnectorInstance(instanceId);
      if (instance.isCredentialValid) {
        setStatus('Connected');
        onConnected?.();
        addNotification(`Successfully connected to ${connectorName}`, 'success');
      } else {
        setStatus('Disconnected');
      }
    } catch (error) {
      setStatus('Error');
    }
  };

  if (isConnected && status === 'Connected') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <ConnectionStatusIndicator status="Connected" />
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleReconnect}
          size="small"
        >
          Reconnect
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        variant="contained"
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
        onClick={handleConnect}
        disabled={loading}
        fullWidth
      >
        {loading ? 'Connecting...' : 'Connect'}
      </Button>
      {status === 'Error' && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Connection failed. Please try again.
        </Alert>
      )}
    </Box>
  );
};
